export function newServiceId() {
  return `svc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
}

export function defaultAlertCues() {
  return [
    { at: 300, message: 'T− 5:00' },
    { at: 60, message: 'T− 1:00' },
    { at: 30, message: 'T− 0:30' },
    { at: 10, message: 'T− 0:10' },
    { at: 0, message: 'GO' },
  ]
}

export function formatCueAt(sec) {
  const n = Math.max(0, Math.round(Number(sec) || 0))
  const m = Math.floor(n / 60)
  const s = n % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export function parseCueAt(raw) {
  const str = String(raw || '').trim()
  if (!str) return 0
  const colon = str.match(/^(\d+):(\d{1,2})$/)
  if (colon) return Math.max(0, Number(colon[1]) * 60 + Number(colon[2]))
  const n = Number(str)
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.round(n)
}

export function defaultCueMessage(at) {
  if (at <= 0) return 'GO'
  return `T− ${formatCueAt(at)}`
}

export function cueAlertStyle(at) {
  if (at <= 0) return { color: '#ef6e6e', icon: 'broadcast-tower' }
  if (at <= 10) return { color: '#ef6e6e', icon: 'exclamation-triangle' }
  if (at <= 60) return { color: '#ffbc34', icon: 'exclamation-triangle' }
  return { color: '#4798e8', icon: 'bell' }
}

export function normalizeAlertCues(raw) {
  if (!Array.isArray(raw)) return defaultAlertCues()
  const seen = new Set()
  const cues = []
  raw.forEach((c) => {
    if (!c || typeof c !== 'object') return
    const at = Math.max(0, Math.round(Number(c.at) || 0))
    if (seen.has(at)) return
    seen.add(at)
    const message = String(c.message || '').trim() || defaultCueMessage(at)
    cues.push({ at, message })
  })
  cues.sort((a, b) => b.at - a.at)
  return cues
}

function clampAutoClose(v) {
  const n = Math.round(Number(v))
  if (!Number.isFinite(n) || n <= 0) return 5
  return Math.min(30, Math.max(1, n))
}

export function normalizeTargetTime(raw) {
  const str = String(raw || '').trim()
  const m = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (!m) return '10:00'
  const h = Math.min(23, Math.max(0, Number(m[1])))
  const min = Math.min(59, Math.max(0, Number(m[2])))
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

export function resolveTargetAt(hhmm, nowMs = Date.now()) {
  const t = normalizeTargetTime(hhmm)
  const [h, min] = t.split(':').map(Number)
  const now = new Date(nowMs)
  const target = new Date(now)
  target.setHours(h, min, 0, 0)
  if (target.getTime() <= nowMs) target.setDate(target.getDate() + 1)
  return target.getTime()
}

export function remainingForPreset(svc, nowMs = Date.now()) {
  if (svc?.tMinusMode === 'wallclock') {
    return Math.round((resolveTargetAt(svc.tMinusTargetTime, nowMs) - nowMs) / 1000)
  }
  return Math.max(1, Number(svc?.tMinusDuration) || 600)
}

export function sessionPatchForPreset(svc) {
  const remaining = remainingForPreset(svc)
  const wallclock = svc?.tMinusMode === 'wallclock'
  return {
    serviceId: svc?.id || null,
    running: false,
    anchorMs: null,
    remainingAtStart: remaining,
    pausedRemaining: wallclock ? null : remaining,
    duration: remaining,
    targetAt: wallclock ? resolveTargetAt(svc.tMinusTargetTime) : null,
    mode: wallclock ? 'wallclock' : 'duration',
    tick: 0,
    alertCuesFired: [],
  }
}

export function blankService(name, index = 1) {
  const label = name || `Countdown ${index}`
  return {
    id: newServiceId(),
    name: label,
    youtubeUrl: '',
    timerLabel: label,
    timerDuration: 600,
    timerMode: 'countdown',
    tMinusLabel: label,
    tMinusDuration: 600,
    tMinusMode: 'duration',
    tMinusTargetTime: '10:00',
    autoStart: false,
    alertCues: defaultAlertCues(),
    alertAutoClose: 5,
  }
}

export function defaultGlobal() {
  return {
    servicePresets: [blankService('Countdown 1', 1), blankService('Countdown 2', 2), blankService('Countdown 3', 3)],
    activeServiceId: null,
  }
}

export function normalizeGlobal(global) {
  const base = global && typeof global === 'object' ? { ...global } : {}
  let presets = Array.isArray(base.servicePresets) ? base.servicePresets : []
  presets = presets
    .filter((p) => p && typeof p === 'object')
    .map((p, i) => ({
      id: String(p.id || newServiceId()),
      name: String(p.name || `Countdown ${i + 1}`).trim() || `Countdown ${i + 1}`,
      youtubeUrl: String(p.youtubeUrl || '').trim(),
      timerLabel: String(p.timerLabel || p.name || `Countdown ${i + 1}`).trim(),
      timerDuration: Math.max(1, Number(p.timerDuration) || 600),
      timerMode: p.timerMode === 'stopwatch' ? 'stopwatch' : 'countdown',
      tMinusLabel: String(p.tMinusLabel || p.name || `Countdown ${i + 1}`).trim(),
      tMinusDuration: Math.max(1, Number(p.tMinusDuration) || Number(p.timerDuration) || 600),
      tMinusMode: p.tMinusMode === 'wallclock' ? 'wallclock' : 'duration',
      tMinusTargetTime: normalizeTargetTime(p.tMinusTargetTime),
      autoStart: p.autoStart === true,
      alertCues: normalizeAlertCues(p.alertCues),
      alertAutoClose: clampAutoClose(p.alertAutoClose),
    }))
  if (!presets.length) {
    presets = defaultGlobal().servicePresets
  }
  let activeServiceId = base.activeServiceId != null ? String(base.activeServiceId) : ''
  if (!presets.some((p) => p.id === activeServiceId)) {
    activeServiceId = presets[0].id
  }
  return { servicePresets: presets, activeServiceId }
}

export function mergeLayoutGlobal(layout) {
  if (!layout || typeof layout !== 'object') return layout
  return {
    ...layout,
    global: normalizeGlobal(layout.global),
  }
}
